import {
	AlertBar,
	Checkbox,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	AdSyncUserProvisioningSettingsSaveModel,
	ManageAdminRoleLocation
} from "client/Api/UserProvisioningSettings/userProvisioningSettings";
import { TokenInput } from "./TokenInput";
import styles from "./UserProvisioningForm.module.less";

export type AdSyncFormProps = {
	attachable: IValueInputAttachable<AdSyncUserProvisioningSettingsSaveModel>;
	hasAToken: boolean;
	onRegenerateToken(): void;
	shouldRenderTheAlert: boolean;
	onAlertDismissed(): void;
};

export const AdSyncForm = (props: AdSyncFormProps): ReactElement => {
	const {
		attachable,
		hasAToken,
		onRegenerateToken,
		shouldRenderTheAlert,
		onAlertDismissed
	} = props;

	const productName = useMemo(
		(): string => {
			return userConfiguration()
				.branding
				.productName;
		},
		[]
	);

	const workspaceUserActiveStatusOptions = useMemo(
		(): readonly RadioButtonGroupItem<boolean>[] => ([
			{
				text: localize("_AzureAD"),
				value: false,
			},
			{
				text: productName,
				value: true,
			}
		]),
		[productName]
	);

	const manageWorkspaceAdminRoleOptions = useMemo(
		(): readonly RadioButtonGroupItem<ManageAdminRoleLocation>[] => ([
			{
				text: localize("_AzureAD"),
				value: ManageAdminRoleLocation.AzureAD,
			},
			{
				text: productName,
				value: ManageAdminRoleLocation.Workspace,
			}
		]),
		[productName]
	);

	return (
		<>
			{shouldRenderTheAlert && (
				<div className={styles.alertContainer}>
					<AlertBar
						kind="info"
						onDismiss={onAlertDismissed}
						isInline
					>
						{localize("_AnAPIKeyWillBeGeneratedInfo")}
					</AlertBar>
				</div>
			)}
			<FormItem>
				<Checkbox
					{...attachable.for("isEnabled")
						.attachToCheckbox()}
					text={localize("_EnableSyncFromAzureAD")}
				/>
				<ValidationError
					{...attachable
						.for("isEnabled")
						.attachToValidationError()}
				/>
			</FormItem>
			<FormItem
				label={{
					template: localize("_ManageAdminRoleFrom"),
					replacements: [
						{
							what: "{ProductName}",
							filler: productName
						}
					]
				}}
				isForGroup
			>
				<RadioButtonGroup
					{...attachable
						.for("manageAdminRoleLocation")
						.attachToRadioButtonGroup()}
					options={manageWorkspaceAdminRoleOptions}
				/>
				<ValidationError
					{...attachable
						.for("manageAdminRoleLocation")
						.attachToValidationError()}
				/>
			</FormItem>
			<FormItem
				label={localize("_ManageActiveStatusFrom")}
				isForGroup
			>
				<RadioButtonGroup
					{...attachable
						.for("canManageUserActiveStatusInWorkspace")
						.attachToRadioButtonGroup()}
					options={workspaceUserActiveStatusOptions}
				/>
				<ValidationError
					{...attachable
						.for("canManageUserActiveStatusInWorkspace")
						.attachToValidationError()}
				/>
			</FormItem>
			<TokenInput
				hasAToken={hasAToken}
				onRegenerateToken={onRegenerateToken}
				tokenLabel={localize("_AzureADSyncAPIKey")}
			/>
		</>
	);
};
AdSyncForm.displayName = "AdSyncForm";